import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed, set } from '@ember/object';
import Component from '@glimmer/component';
import { PrismPerfectScrollbar, styleEntry } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import GeneralTablesCellsCellDateOverview from 'lumen/components/general/tables/cells/cell-date-overview';
import GeneralTablesCellsCellHotelRate from 'lumen/components/general/tables/cells/cell-hotel-rate';
import GeneralTablesCellsCellLiveView from 'lumen/components/general/tables/cells/cell-live-view';
import GeneralTablesCellsCellMetaOta from 'lumen/components/general/tables/cells/cell-meta-ota';
import GeneralTablesCellsCellOta from 'lumen/components/general/tables/cells/cell-ota';
import GeneralTablesCellsCellOtaRate from 'lumen/components/general/tables/cells/cell-ota-rate';
import GeneralTablesCellsCellRate from 'lumen/components/general/tables/cells/cell-rate';
import type { Parity } from 'lumen/models';

const VISIBLE_ROWS = 10;
const ROW_HEIGHT = 35;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function sort(issues = []) {
  return [...issues].sort((a, b) => {
    // @ts-expect-error: Incorrect type
    return a.differenceWithBaserate - b.differenceWithBaserate;
  });
}

type EventPrefix =
  | 'hotel-parity-overview-loss-brand_closed'
  | 'hotel-parity-overview-loss-brand_mobile'
  | 'hotel-parity-overview-loss-brand_nonref'
  | 'hotel-parity-overview-loss-brand_roomtype'
  | 'hotel-parity-overview-loss-other'
  | 'hotel-parity-overview-win-brand_cheaper';

interface GeneralTablesIssueTableOverviewSignature {
  Args: {
    params: {
      eventPrefix: EventPrefix;
      id: 'brand_cheaper' | 'wholesalers';
      issues: Parity[];
      paritiesBaseratesDict: unknown;
    };
  };
}

export default class GeneralTablesIssueTableOverview extends Component<GeneralTablesIssueTableOverviewSignature> {
  // Enable day shops for all subscriptions
  dayLiveupdateEnabled = true;
  endIndex = VISIBLE_ROWS * 3;
  startIndex = 0;

  @computed('args.params.issues')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get issues() {
    // @ts-expect-error: Incorrect type
    return sort(this.args.params?.issues ?? []);
  }

  @computed('args.params.id')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isWholesale() {
    return this.args.params?.id === 'wholesalers';
  }

  @computed('args.params.id')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isWin() {
    return this.args.params?.id === 'brand_cheaper';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tableHeight() {
    return `${Math.min(this.issues?.length, 10) * ROW_HEIGHT}px`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleIssues() {
    return this.issues.slice(this.startIndex, this.endIndex);
  }

  @action
  // @ts-expect-error: Incorrect type
  onEndReached(perfectScrollbar, evt): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const scrollableContainer = evt.currentTarget;
    const currentStartIndex = this.startIndex;
    const currentEndIndex = this.endIndex;
    // only if we have't reached the end
    if (currentEndIndex < this.issues?.length) {
      set(this, 'startIndex', currentStartIndex + VISIBLE_ROWS);
      set(this, 'endIndex', currentEndIndex + VISIBLE_ROWS);
      // move scroll 10 rows up
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      scrollableContainer.scrollTop -= VISIBLE_ROWS * ROW_HEIGHT;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      perfectScrollbar.update();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onStartReached(perfectScrollbar, evt): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const scrollableContainer = evt.currentTarget;
    const currentStartIndex = this.startIndex;
    const currentEndIndex = this.endIndex;
    // only if we haven't reached the start
    if (currentStartIndex > 0) {
      set(this, 'startIndex', currentStartIndex - VISIBLE_ROWS);
      set(this, 'endIndex', currentEndIndex - VISIBLE_ROWS);
      // move scroll 10 rows down
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      scrollableContainer.scrollTop += VISIBLE_ROWS * ROW_HEIGHT;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      perfectScrollbar.update();
    }
  }

  <template>
    {{! @glint-nocheck }}
    <div class="card-tile">
      <div
        class="parity-issue-table-header-container"
        id="parity-issue-table-header-container"
      >
        <table
          cellpadding="0"
          cellspacing="0"
          class="parity-issue-table-header parity-issue-table default-table default-table--fixed"
          id="parity-issue-table-header"
        >
          <thead>
            <tr>
              <th class="date text-data">
                {{t "general.date"}}
              </th>
              <th class="text-data">
                {{t "parity.source"}}
              </th>
              <th class="text-data">
                {{t "general.ota"}}
              </th>
              <th>
                {{t "parity.pos.hotel_rate"}}
              </th>
              <th>
                {{t "parity.ota_rate"}}
              </th>
              <th>
                {{#if this.isWin}}
                  {{t "parity_analytics.win"}}
                {{else}}
                  {{t "parity_analytics.loss"}}
                {{/if}}
              </th>
              <th class="live-view">
                <div class="flex items-center justify-center gap-100">
                  {{t "parity.pos.live"}}
                  <GeneralIconWithTooltip
                    @side="top"
                    @tooltipContent={{t "parity.live_view_tooltip"}}
                    @tooltipTitle={{t "parity.live_view"}}
                  />
                </div>
              </th>
            </tr>
          </thead>
        </table>
      </div>
      <PrismPerfectScrollbar
        @customContainerId="parity-issue-table-body-container-{{this.elementId}}"
        as |perfectScrollbar|
      >
        <div
          class="parity-issue-table-body-container"
          id="parity-issue-table-body-container-{{this.elementId}}"
          style={{styleEntry "height" this.tableHeight}}
          {{on "ps-y-reach-end" (fn this.onEndReached perfectScrollbar)}}
          {{on "ps-y-reach-start" (fn this.onStartReached perfectScrollbar)}}
        >
          <table
            cellpadding="0"
            cellspacing="0"
            class="parity-issue-table-body parity-issue-table default-table default-table--fixed"
            id="parity-issue-table-body"
          >
            <tbody>
              {{#each this.visibleIssues as |issue|}}
                <tr>
                  <td class="date text-data">
                    <GeneralTablesCellsCellDateOverview
                      @hasDayLiveShop={{this.dayLiveupdateEnabled}}
                      @parity={{issue}}
                      @period={{issue.period}}
                    />
                  </td>
                  <td class="text-data">
                    <GeneralTablesCellsCellMetaOta
                      @ota={{issue.channel}}
                      @source={{issue.ota.id}}
                    />
                  </td>
                  <td class="text-data right-divider">
                    <GeneralTablesCellsCellOta @value={{issue.channel}} />
                  </td>
                  <td class="numeric-data">
                    <GeneralTablesCellsCellHotelRate
                      @paritiesBaseratesDict={{@params.paritiesBaseratesDict}}
                      @periodId={{issue.period.id}}
                    />
                  </td>
                  <td class="numeric-data right-divider">
                    <GeneralTablesCellsCellOtaRate @issue={{issue}} />
                  </td>
                  <td class="numeric-data right-divider">
                    <GeneralTablesCellsCellRate
                      @rate={{issue.differenceWithBaserate}}
                    />
                  </td>
                  {{#if @params.eventPrefix}}
                    <td
                      class="live-view numeric-data"
                      data-event-id={{concat @params.eventPrefix "-live-link"}}
                      data-event-value={{concat
                        issue.period.day.date
                        " | "
                        issue.ota.id
                        " | "
                        issue.differenceWithBaserate
                      }}
                    >
                      <GeneralTablesCellsCellLiveView
                        @period={{issue.period}}
                        @persons={{issue.maxPersons}}
                        @source={{issue.ota.id}}
                      />
                    </td>
                  {{else}}
                    <td class="live-view numeric-data">
                      <GeneralTablesCellsCellLiveView
                        @period={{issue.period}}
                        @persons={{issue.maxPersons}}
                        @source={{issue.ota.id}}
                      />
                    </td>
                  {{/if}}
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
      </PrismPerfectScrollbar>
    </div>
  </template>
}
